import React, { useEffect, useState } from 'react'
import Sheet from '@mui/joy/Sheet'
import Box from '@mui/joy/Box'
import { EditorPanel } from './EditorPanel'
import { ObjectPanel } from './ObjectPanel'
import { Error } from './ui/Error'
import { TemplateAPI } from '../api/TemplateAPI'
import { Canvas } from './ui/CanvasV2'
import { CanvasContainer } from './CanvasContainer'
import { canvasUtils } from '../common/canvasUtils'
import { Loading } from './ui/Loading'
import { FontLoader } from './ui/FontLoader'
import _ from 'lodash'

const SQUARE_TEMPLATE_SIZE = 600
const CANVAS_FILL_PERCENTAGE = 70

export const Editor = ({ id, postData, onChange }) => {
    const [selectedObj, setSelectedObj] = useState(null)
    const [canvasJSON, setCanvasJSON] = useState(null)
    const [isError, setIsError] = useState(false)
    const [isLoading, setIsLoading] = useState(false)
    const [isFontsReady, setIsFontsReady] = useState(false)
    const [fonts, setFonts] = useState([])
    const [canvas, setCanvas] = useState(null)
    const [template, setTemplate] = useState(null)
    const [dimensions, setDimensions] = useState({
        width: SQUARE_TEMPLATE_SIZE,
        height: SQUARE_TEMPLATE_SIZE,
        scale: 1,
    })

    const _replaceTemplateData = (template) => {
        const object = template.objects.find(
            (obj) => obj.type === 'template-textbox' && obj.name === 'str_quote'
        )
        if (object) {
            const tb = canvasUtils.maxFontTextbox(
                postData,
                object.width,
                object.height,
                object.fontFamily,
                object.fontWeight
            )
            object.fontSize = tb.fontSize
            object.text = postData
        }
        return { ...template }
    }

    const handleTemplateChange = (id) => {
        setSelectedObj(null)
        setIsLoading(true)
        setIsFontsReady(false)
        TemplateAPI.get(id)
            .then((response) => {
                setTemplate(response.data.canvas)
            })
            .catch((error) => console.log(error))
    }

    const handleObjDeleted = () => {
        setSelectedObj(null)
    }

    const handleObjSelected = ({ selected }) => {
        setSelectedObj(selected[0])
    }

    const handleCanvasChange = (e) => {
        onChange(id, e.target.canvas)
    }

    const handlePanelChange = (canvas) => {
        onChange(id, canvas)
    }

    const renderCanvas = async (canvas) => {
        return _replaceTemplateData(canvas)
    }

    useEffect(() => {
        setIsLoading(true)
        if (template) {
            let nextFonts = canvasUtils.findAllFonts(template)
            if (!_.isEqual(nextFonts, fonts)) {
                setFonts(canvasUtils.findAllFonts(template))
            } else {
                renderCanvas(template).then((interpolated) => {
                    setCanvasJSON(interpolated)
                    setIsLoading(false)
                })
            }
        }
    }, [template, postData])

    useEffect(() => {
        if (isFontsReady) {
            renderCanvas(template).then((interpolated) => {
                setCanvasJSON(interpolated)
                setIsLoading(false)
            })
        }
    }, [isFontsReady])

    return (
        <Sheet
            sx={{
                height: '100%',
                display: 'flex',
                flexGrow: 1,
                flexDirection: 'row',
                borderWidth: '0 1px 0 0',
            }}
            variant="outlined"
            color="neutral"
        >
            {isError ? (
                <Error />
            ) : (
                <>
                    <EditorPanel
                        canvas={canvas}
                        onTemplateChange={handleTemplateChange}
                    />
                    <Box
                        sx={{
                            backgroundColor: '#EBECF0',
                            display: 'flex',
                            flexDirection: 'column',
                            flexGrow: 1,
                        }}
                    >
                        <Box>
                            <ObjectPanel
                                canvas={canvas}
                                obj={selectedObj}
                                onDelete={handleObjDeleted}
                                onChange={handlePanelChange}
                            />
                        </Box>
                        <FontLoader
                            fontDataArr={fonts}
                            onStatusChanged={() => setIsFontsReady(true)}
                        />
                        {canvasJSON && !isLoading ? (
                            <CanvasContainer
                                fillPercentage={CANVAS_FILL_PERCENTAGE}
                                originalWidth={SQUARE_TEMPLATE_SIZE}
                                originalHeight={SQUARE_TEMPLATE_SIZE}
                                onResize={(data) => setDimensions(data)}
                            >
                                <Canvas
                                    width={dimensions.width}
                                    height={dimensions.height}
                                    scale={dimensions.scale}
                                    onObjectAdded={handleCanvasChange.bind(
                                        canvas
                                    )}
                                    onObjectModified={handleCanvasChange.bind(
                                        canvas
                                    )}
                                    onObjectRemoved={handleCanvasChange.bind(
                                        canvas
                                    )}
                                    onSelectionCreated={handleObjSelected}
                                    onSelectionUpdated={handleObjSelected}
                                    onSelectionCleared={() =>
                                        console.log('selection cleared')
                                    }
                                    canvasJSON={canvasJSON}
                                    ref={(canvas) => {
                                        setCanvas(canvas)
                                    }}
                                />
                            </CanvasContainer>
                        ) : (
                            <Loading />
                        )}
                    </Box>
                </>
            )}
        </Sheet>
    )
}
